import React, { useState, useEffect, useRef } from "react";

// Pactly Cloud Intelligence — Aurora Pulse component kit.
export const C = {
  ev: "#00D4FF",
  ice: "#C0C8D4",
  healthy: "#00E5A0",
  caution: "#FFB020",
  critical: "#FF3B5C",
  gold: "#D4AF37",
  bg: "#0a0a1a",
  card: "#12122a",
  silver: "#C0C8D4",
};

export function gradeMeta(soh) {
  if (soh == null) return { grade: "Inconnu", color: C.silver, emoji: "⚪" };
  if (soh >= 90) return { grade: "Excellent", color: C.healthy, emoji: "🟢" };
  if (soh >= 80) return { grade: "Bon", color: C.healthy, emoji: "🟢" };
  if (soh >= 70) return { grade: "Correct", color: C.caution, emoji: "🟡" };
  if (soh >= 60) return { grade: "Usée", color: C.caution, emoji: "🟠" };
  return { grade: "Critique", color: C.critical, emoji: "🔴" };
}

export function trustMeta(score) {
  if (score >= 80) return { color: C.healthy, label: "Fiable" };
  if (score >= 50) return { color: C.caution, label: "À vérifier" };
  return { color: C.critical, label: "Risque" };
}

// 1. Animated pulsing ring shown while a cloud scan is decoding.
export function AuroraPulse({ active = true, size = 120, label = "" }) {
  return (
    <div data-testid="aurora-pulse" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <div style={{ position: "relative", width: size, height: size }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{
            position: "absolute", inset: 0, borderRadius: "50%",
            border: `2px solid ${C.ev}`,
            animation: active ? `aurora-ring 1.8s ${i * 0.5}s ease-out infinite` : "none",
            opacity: active ? 0.7 : 0.2,
          }} />
        ))}
        <svg width={size} height={size} viewBox="0 0 120 120" style={{ position: "relative" }}>
          <defs>
            <linearGradient id="aurora-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={C.healthy} />
              <stop offset="100%" stopColor={C.ev} />
            </linearGradient>
          </defs>
          <circle cx="60" cy="60" r="42" fill="none" stroke="url(#aurora-grad)" strokeWidth="5"
            strokeLinecap="round" strokeDasharray="200 264"
            style={{ transformOrigin: "center", animation: active ? "aurora-spin 1.2s linear infinite" : "none",
              filter: `drop-shadow(0 0 8px ${C.ev})` }} />
          <circle cx="60" cy="60" r="30" fill="none" stroke={`${C.healthy}55`} strokeWidth="2" />
        </svg>
      </div>
      {label ? <div style={{ fontSize: 13, color: C.ev, fontWeight: 600 }}>{label}</div> : null}
      <style>{`
        @keyframes aurora-ring { 0% { transform: scale(0.7); opacity: 0.8 } 100% { transform: scale(1.35); opacity: 0 } }
        @keyframes aurora-spin { to { transform: rotate(360deg) } }
      `}</style>
    </div>
  );
}

// 2. Circular SOH gauge (270° arc).
export function SOHGauge({ soh, size = 160, vehicleType = "ev" }) {
  const meta = gradeMeta(soh);
  const r = (size - 26) / 2, cx = size / 2, cy = size / 2;
  const START = 135, SWEEP = 270;
  const pct = Math.max(0, Math.min(100, soh ?? 0)) / 100;
  const polar = (deg) => {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };
  const arc = (fromPct, toPct, color, width) => {
    const a0 = START + SWEEP * fromPct, a1 = START + SWEEP * toPct;
    const [x0, y0] = polar(a0), [x1, y1] = polar(a1);
    const large = a1 - a0 > 180 ? 1 : 0;
    return <path d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`} fill="none"
      stroke={color} strokeWidth={width} strokeLinecap="round" />;
  };
  return (
    <div data-testid="soh-gauge" style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size}>
        {arc(0, 1, "#ffffff14", 12)}
        {soh != null && arc(0, pct, meta.color, 12)}
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: size * 0.26, fontWeight: 800, color: meta.color, fontFamily: "JetBrains Mono, monospace", lineHeight: 1 }}>
          {soh != null ? `${soh}%` : "—"}
        </span>
        <span style={{ fontSize: 12, color: C.silver, marginTop: 4 }}>SOH · {meta.grade}</span>
        <span style={{ fontSize: 10, color: vehicleType === "ev" ? C.ev : C.silver, marginTop: 2, textTransform: "uppercase", letterSpacing: 1 }}>{vehicleType}</span>
      </div>
    </div>
  );
}

// 3. Trust score badge.
export function TrustScoreBadge({ score }) {
  if (score == null) return null;
  const m = trustMeta(score);
  return (
    <span data-testid="trust-score-badge" style={{
      display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20,
      background: `${m.color}18`, border: `1px solid ${m.color}`, color: m.color, fontWeight: 700,
      fontSize: 12, fontFamily: "JetBrains Mono, monospace",
    }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: m.color, boxShadow: `0 0 8px ${m.color}` }} />
      Trust {score}/100 · {m.label}
    </span>
  );
}

// 4. Factory options grid.
const OPT_ICON = (o) => {
  const s = o.toLowerCase();
  if (s.includes("toit") || s.includes("roof")) return "🌤️";
  if (s.includes("autopilot") || s.includes("pilot")) return "🛰️";
  if (s.includes("jante") || s.includes("wheel")) return "🛞";
  if (s.includes("cuir") || s.includes("leather")) return "🪑";
  if (s.includes("son") || s.includes("audio")) return "🔊";
  if (s.includes("nav") || s.includes("gps")) return "🧭";
  if (s.includes("cam")) return "📷";
  return "✔️";
};
export function FactoryOptionsGrid({ options }) {
  if (!options || !options.length) return null;
  return (
    <div data-testid="factory-options-grid">
      <div style={{ fontSize: 12, color: C.silver, opacity: 0.7, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
        Options d'usine détectées (OBD)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
        {options.map((o, i) => (
          <div key={i} data-testid={`factory-option-${i}`} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 10,
            background: `${C.ev}10`, border: `1px solid ${C.ev}35`, fontSize: 13, color: "#fff",
          }}>
            <span style={{ fontSize: 15 }}>{OPT_ICON(o)}</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 5. Battery heritage SVG line chart (SOH over km/time).
export function BatteryHeritageLine({ scans, analysis }) {
  if (!scans || scans.length < 2) {
    return <div data-testid="battery-heritage-empty" style={{ fontSize: 13, color: C.silver, opacity: 0.6, padding: 16 }}>
      Historique en cours de constitution (1 scan). Un 2ᵉ diagnostic activera la courbe de dégradation.
    </div>;
  }
  const w = 560, h = 180, pad = 34;
  const ys = scans.map((s) => s.soh_percent);
  const minY = Math.min(...ys) - 3, maxY = Math.max(...ys) + 3;
  const px = (i) => pad + (i / (scans.length - 1)) * (w - pad * 2);
  const py = (v) => h - pad - ((v - minY) / (maxY - minY || 1)) * (h - pad * 2);
  const path = scans.map((s, i) => `${i === 0 ? "M" : "L"}${px(i)},${py(s.soh_percent)}`).join(" ");
  return (
    <div data-testid="battery-heritage-line">
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ background: "#ffffff06", borderRadius: 12, border: "1px solid #ffffff12" }}>
        <path d={`${path} L ${px(scans.length - 1)},${h - pad} L ${px(0)},${h - pad} Z`} fill={`${C.ev}12`} />
        <path d={path} fill="none" stroke={C.healthy} strokeWidth={2.5} style={{ filter: `drop-shadow(0 0 4px ${C.healthy})` }} />
        {scans.map((s, i) => (
          <g key={i}>
            <circle cx={px(i)} cy={py(s.soh_percent)} r={4} fill={C.ev} />
            <text x={px(i)} y={py(s.soh_percent) - 10} fill="#fff" fontSize="11" textAnchor="middle" fontFamily="JetBrains Mono, monospace">{s.soh_percent}%</text>
          </g>
        ))}
      </svg>
      {analysis?.degradation_rate_per_10k_km != null && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12 }}>
          <HeritageStat label="Δ SOH" value={`${analysis.delta_soh}%`} />
          <HeritageStat label="Δ Km" value={analysis.delta_km ? `${analysis.delta_km.toLocaleString("fr-FR")} km` : "—"} />
          <HeritageStat label="Dégradation" value={`${analysis.degradation_rate_per_10k_km}%/10 000 km`} />
          <HeritageStat label="Remplacement estimé" value={analysis.estimated_replacement_km ? `${analysis.estimated_replacement_km.toLocaleString("fr-FR")} km` : "—"} />
        </div>
      )}
    </div>
  );
}
const HeritageStat = ({ label, value }) => (
  <div style={{ background: "#ffffff08", borderRadius: 10, padding: "8px 12px", border: "1px solid #ffffff12" }}>
    <div style={{ fontSize: 10, color: C.silver, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", fontFamily: "JetBrains Mono, monospace" }}>{value}</div>
  </div>
);

// 6. Compact certificate card.
export function HealthPassportCard({ cert, onClick, onHeritage, onBuyGA }) {
  const meta = gradeMeta(cert.soh_percent);
  const vt = cert.vehicle_type || "ev";
  const ga = cert.grade_assurance || {};
  return (
    <div data-testid={`health-passport-${cert.cert_id || cert.report_id}`} onClick={onClick} style={{
      display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 16, alignItems: "center", cursor: "pointer",
      background: C.card, border: `1px solid ${cert.fraud_flags?.length ? C.critical + "55" : (ga.certified ? C.gold + "55" : "#ffffff14")}`,
      borderRadius: 16, padding: "16px 20px",
    }}>
      <SOHGauge soh={cert.soh_percent} size={84} vehicleType={vt} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", fontFamily: "Sora, sans-serif" }}>
          {cert.vehicle?.make} {cert.vehicle?.model} {cert.vehicle?.year || ""}
        </div>
        <div style={{ fontSize: 12, color: C.silver, opacity: 0.7, fontFamily: "JetBrains Mono, monospace", marginBottom: 8 }}>
          VIN {cert.vin} · #{cert.cert_id || cert.report_id}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: meta.color, fontWeight: 700 }}>{meta.emoji} {meta.grade}</span>
          <TrustScoreBadge score={cert.trust_score} />
          {cert.factory_options?.length > 0 && (
            <span style={{ fontSize: 11, color: C.ev }}>+{cert.factory_options.length} options OBD</span>
          )}
          {cert.mileage_verified_km != null && (
            <span style={{ fontSize: 11, color: C.silver }}>{cert.mileage_verified_km.toLocaleString("fr-FR")} km vérifiés</span>
          )}
        </div>
        <div style={{ marginTop: 8 }}>
          {ga.certified ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <GradeAssuranceBadge grade={ga.grade} compact />
              {ga.valid_until && <span style={{ fontSize: 10, color: C.silver, opacity: 0.7 }}>valide jusqu'au {String(ga.valid_until).slice(0, 10)}</span>}
            </span>
          ) : cert.status === "pending_payment" ? (
            <span style={{ fontSize: 11, color: C.caution, fontWeight: 700 }}>🟡 En attente de paiement</span>
          ) : onBuyGA ? (
            <button data-testid={`buy-ga-${cert.cert_id || cert.report_id}`} onClick={(e) => { e.stopPropagation(); onBuyGA(cert); }}
              style={{ background: `${C.gold}18`, color: C.gold, border: `1px solid ${C.gold}`, borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
              🛡️ Certifier · Grade Assurance 49€
            </button>
          ) : null}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
        {onHeritage && (
          <button data-testid={`heritage-btn-${cert.cert_id || cert.report_id}`} onClick={(e) => { e.stopPropagation(); onHeritage(cert); }}
            style={{ background: `${C.ev}18`, color: C.ev, border: `1px solid ${C.ev}55`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            📈 Heritage
          </button>
        )}
        <a data-testid={`cert-pdf-${cert.cert_id || cert.report_id}`} onClick={(e) => e.stopPropagation()}
          href={`${process.env.REACT_APP_BACKEND_URL}/api/verify/${cert.cert_id || cert.report_id}/pdf?origin=${encodeURIComponent(window.location.origin)}`}
          target="_blank" rel="noopener noreferrer"
          style={{ color: C.gold, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>⬇ PDF</a>
      </div>
    </div>
  );
}

// 7. Suggestion banner for EV/hybrid listings without a certificate.
export function BatteryBlockBanner({ vehicleType = "ev", onScan, dismissible = true }) {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;
  return (
    <div data-testid="battery-block-banner" style={{
      display: "flex", alignItems: "flex-start", gap: 14, padding: "16px 18px", borderRadius: 14,
      background: `linear-gradient(120deg, ${C.card}, ${C.ev}12)`, border: `1px solid ${C.ev}45`,
    }}>
      <span style={{ fontSize: 24 }}>🔋</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 800, color: "#fff", fontSize: 15, marginBottom: 4 }}>
          Boostez la vente : ajoutez un diagnostic batterie
        </div>
        <div style={{ fontSize: 13, color: C.silver, lineHeight: 1.5 }}>
          Ce véhicule {vehicleType === "hybrid" ? "hybride" : "électrique"} n'a pas encore de certificat Cloud Scan OBD.
          Les annonces certifiées inspirent davantage confiance et se vendent plus vite et plus cher.
        </div>
        {onScan && (
          <button data-testid="battery-block-scan-cta" onClick={onScan} style={{
            marginTop: 10, background: `linear-gradient(135deg, ${C.healthy}, ${C.ev})`, color: "#04121a",
            border: "none", borderRadius: 10, padding: "8px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}>Lancer un Cloud Scan</button>
        )}
      </div>
      {dismissible && (
        <button data-testid="battery-block-dismiss" onClick={() => setHidden(true)} style={{ background: "transparent", border: "none", color: C.silver, fontSize: 18, cursor: "pointer" }}>×</button>
      )}
    </div>
  );
}

// Heritage modal wrapper (loads /api/battery/heritage/{vin}).
export function HeritageModal({ vin, onClose, crm }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const d = await crm(`/battery/heritage/${vin}`, { noCache: true }); if (alive) setData(d); }
      catch { if (alive) setData({ scans: [], analysis: {} }); }
    })();
    return () => { alive = false; };
  }, [vin, crm]);
  return (
    <div data-testid="heritage-modal" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 120, background: "rgba(4,6,14,0.85)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 640, background: C.bg, border: `1px solid ${C.ev}40`, borderRadius: 20, padding: 26 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, color: "#fff", fontFamily: "Sora, sans-serif" }}>Battery Heritage — {vin}</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.silver, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {!data ? <AuroraPulse label="Chargement de l'historique…" size={90} />
          : <BatteryHeritageLine scans={(data.scans || []).filter((s) => s.soh_percent != null)} analysis={data.analysis} />}
      </div>
    </div>
  );
}

// ─── GRADE ASSURANCE premium components ────────────────────────────────────
const GRADE_COLOR = { A: "#00E5A0", B: "#00E5A0", C: C.caution, D: C.critical };
export const GA_WORDING = "Certificat SOH Officiel : Méthodologie certifiée et reconnue par les compagnies d'assurance européennes";

// Rotating/pulsing gold "Expertise Digitale" seal (web).
export function ExpertiseSeal({ size = 92, certId = "", date = "" }) {
  const text = "EXPERTISE DIGITALE PACTLY CAR · ";
  return (
    <div data-testid="expertise-seal" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{ position: "relative", width: size, height: size, animation: "ga-glow 2.6s ease-in-out infinite" }}>
        <svg width={size} height={size} viewBox="0 0 100 100">
          <defs><path id="ga-seal-path" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" /></defs>
          <circle cx="50" cy="50" r="46" fill="none" stroke={C.gold} strokeWidth="1.5" opacity="0.5" />
          <circle cx="50" cy="50" r="30" fill="none" stroke={C.gold} strokeWidth="1" opacity="0.7" />
          <g style={{ transformOrigin: "50px 50px", animation: "ga-spin 14s linear infinite" }}>
            <text fill={C.gold} fontSize="7.2" fontWeight="700" letterSpacing="0.5">
              <textPath href="#ga-seal-path" startOffset="0">{text + text}</textPath>
            </text>
          </g>
          <text x="50" y="59" textAnchor="middle" fill={C.gold} fontSize="34" fontWeight="800" fontFamily="Sora, sans-serif">P</text>
        </svg>
      </div>
      {certId ? <div style={{ fontSize: 10, color: C.silver, fontFamily: "JetBrains Mono, monospace" }}>{certId}{date ? ` · ${String(date).slice(0, 10)}` : ""}</div> : null}
      <style>{`
        @keyframes ga-spin { to { transform: rotate(360deg) } }
        @keyframes ga-glow { 0%,100% { filter: drop-shadow(0 0 4px ${C.gold}55) } 50% { filter: drop-shadow(0 0 14px ${C.gold}) } }
      `}</style>
    </div>
  );
}

// Gold shield badge: "Certificat SOH Officiel" + grade letter.
export function GradeAssuranceBadge({ grade, gradeLabel, compact = false, onClick }) {
  const col = GRADE_COLOR[grade] || C.gold;
  return (
    <span data-testid="grade-assurance-badge" onClick={onClick} title={GA_WORDING} style={{
      display: "inline-flex", alignItems: "center", gap: 8, padding: compact ? "4px 10px" : "6px 14px",
      borderRadius: 12, background: `linear-gradient(120deg, ${C.gold}22, ${col}18)`,
      border: `1px solid ${C.gold}`, cursor: onClick ? "pointer" : "default",
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2l7 3v6c0 4.5-3 8-7 11-4-3-7-6.5-7-11V5l7-3z" fill={C.gold} opacity="0.9" /><path d="M9 12l2 2 4-4" stroke="#0A0E1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      <span style={{ fontSize: compact ? 11 : 13, fontWeight: 800, color: C.gold }}>Certificat SOH Officiel</span>
      {grade ? <span style={{ fontSize: compact ? 11 : 13, fontWeight: 800, color: col, background: `${col}22`, borderRadius: 6, padding: "1px 7px" }}>Grade {grade}</span> : null}
    </span>
  );
}

// Subtle aurora tag shown next to the price.
export function PriceJustificationTag() {
  return (
    <span data-testid="price-justification-tag" style={{
      display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20,
      background: `${C.ev}12`, border: `1px solid ${C.ev}40`, fontSize: 11, color: C.ev, fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.ev, boxShadow: `0 0 6px ${C.ev}` }} />
      Prix justifié par Certificat SOH Grade Assurance
    </span>
  );
}

// Auto-Fill 360 review panel: shows what was auto-filled with source badges.
const SRC = { obd: { icon: "🔌", label: "OBD Direct", color: C.ev }, api: { icon: "🌐", label: "API Constructeur", color: C.healthy }, seller: { icon: "📋", label: "Déclaration vendeur", color: C.silver } };
export function AutoFill360Panel({ fields = [], onConfirm }) {
  // fields: [{label, value, source: 'obd'|'api'|'seller', locked}]
  return (
    <div data-testid="autofill360-panel" style={{ background: C.card, border: `1px solid ${C.ev}30`, borderRadius: 16, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 20 }}>✨</span>
        <div>
          <div style={{ fontWeight: 800, color: "#fff", fontSize: 15 }}>Auto-Fill 360</div>
          <div style={{ fontSize: 12, color: C.silver, opacity: 0.7 }}>Champs pré-remplis depuis le diagnostic — vérifiez avant publication.</div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {fields.map((f, i) => {
          const s = SRC[f.source] || SRC.seller;
          return (
            <div key={i} data-testid={`autofill-field-${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", borderRadius: 10, background: "#ffffff06", border: "1px solid #ffffff10" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: C.silver, opacity: 0.7 }}>{f.label}</div>
                <div style={{ fontSize: 14, color: "#fff", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  {f.locked && <span title="Champ certifié verrouillé">🔒</span>}
                  {Array.isArray(f.value) ? f.value.join(", ") : String(f.value ?? "—")}
                </div>
              </div>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 16, background: `${s.color}15`, border: `1px solid ${s.color}45`, fontSize: 11, color: s.color, fontWeight: 700, whiteSpace: "nowrap" }}>
                {s.icon} {s.label}
              </span>
            </div>
          );
        })}
      </div>
      {onConfirm && (
        <button data-testid="autofill360-confirm" onClick={onConfirm} style={{ marginTop: 14, width: "100%", background: `linear-gradient(135deg, ${C.healthy}, ${C.ev})`, color: "#04121a", border: "none", borderRadius: 10, padding: "11px", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
          Confirmer et publier
        </button>
      )}
    </div>
  );
}
